import { ProfileMenu } from '@/features/common/layout/ProfileMenu';
import { Suspense, useEffect } from 'react';
import { RouteFallback } from '@/app/RouteFallback';
import { Outlet, ScrollRestoration } from 'react-router-dom';
import { useSession } from '@/app/session';
import { primaryRole } from '@/features/common/labels';
import { appShell } from '@/i18n/dictionaries/appShell';
import { roleLabel } from '@/i18n/roleLabels';
import { useTranslation } from '@/i18n/useTranslation';
import { Icon } from '@/features/common/Icon';
import { PAGE_VIEW, usePageViewCounter } from '@/features/common/usePageViewCounter';
import { SITE_PAGE } from '@/features/common/layout/publicPageLinks';
import '@/app/shells/shell.css';
import '@/features/common/layout/gov-bar.css';
import '@/features/common/layout/site-header.css';

// Chrome-only shell for single-flow pages (a wizard, a read-only dossier view) that don't
// belong in the sidebar's page hierarchy — same pre-header/header/footer as AppShell, no
// sidebar, content capped at 1160px. AppShell's chrome markup is duplicated here rather than
// extracted into a shared component: the two have diverged in what they render around the
// content (drawer/nav vs. none) since AppShell's version, and a shared wrapper would need to
// branch on "has sidebar" anyway — simpler to keep the two shells as siblings.
export function FlowShell() {
  const { user } = useSession();
  const { t, language } = useTranslation(appShell);
  const displayRole = user ? primaryRole(user.roles) : undefined;
  // The two dossier routes live here rather than under AppShell, and they are cabinet pages like
  // any other — counted by the same report (T236).
  usePageViewCounter(PAGE_VIEW.cabinet);

  useEffect(() => {
    document.title = t('pageTitleSuffix');
  }, [t]);

  return (
    <div className="app-shell">
      <ScrollRestoration />
      <a className="skip-link" href="#main">
        {t('skipLink')}
      </a>
      <div className="gov-bar">
        <div className="gov-bar__inner">
          <p className="gov-bar__official">
            <Icon name="arrow-left-gov" className="gov-bar__mark" />
            {t('govBarOfficial')}
          </p>
        </div>
      </div>
      <header className="site-header">
        <div className="site-header__inner">
          <a href="/" className="brand" aria-label={t('brandAria')}>
            <span className="brand__mark" aria-hidden="true">
              <span className="brand__mark-text">AK</span>
            </span>
            <span className="brand__word" aria-hidden="true">
              AKIS
            </span>
          </a>
          <span className="site-header__zone">{t('zoneLabel')}</span>
          <div className="header-actions">
            {/* Shared with AppShell and the public header — features/common/layout/ProfileMenu. */}
            {user && (
              <ProfileMenu
                fullName={user.fullName}
                roleLabel={displayRole ? roleLabel(displayRole, language) : null}
                profileHref="/app/profil"
              />
            )}
          </div>
        </div>
      </header>
      <div className="flow-layout">
        <main className="app-main" id="main">
          <Suspense fallback={<RouteFallback />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
      <footer className="app-footer">
        <div className="app-footer__links">
          <a href="/politici/utilizare">{t('footerTerms')}</a>
          <a href="/politici/confidentialitate">{t('footerPrivacy')}</a>
          <a href="/politici/cookies">{t('footerCookies')}</a>
          <a href={SITE_PAGE.accessibility}>{t('footerAccessibility')}</a>
        </div>
        <span className="app-footer__copyright">{t('footerCopyright')}</span>
      </footer>
    </div>
  );
}
